import { CountryResponseDto } from '../../infra/repositories/dto/res/CountryResponseDto';
import { SectionOneFormState } from '../domain/models/section-one-form-state';
import SectionOne from '../domain/entities/SectionOne';
import { SectionOneSteps } from '../domain/models/section-one-steps';
import { ISectionOneRepository } from '../ports/outbound/ISectionOneRepository';
import { SectionOneForm } from '../validators/section-one.schema';
import { EmployeeProvidedDocs } from '../domain/entities/EmployeeProvidedDocs';
import { SaveEmployeeProvidedDocsCommand } from '../domain/entities/SaveEmployeeProvidedDocsCommand';
import { SubmitSectionOneResult } from '../domain/entities/SubmitSectionOneResult';
import { InviteValidationResult } from '../domain/entities/InviteValidationResult';

export class SectionOneUseCase {
  constructor(private repo: ISectionOneRepository) {}

  async submitSectionOne(
    data: SectionOneForm,
    status: SectionOneFormState,
    i9FormId?: string
  ): Promise<SubmitSectionOneResult> {
    return await this.repo.submitSectionOne(data, status, i9FormId);
  }

  async validateInvite(encryptedI9FormId: string): Promise<InviteValidationResult> {
    return await this.repo.validateInvite(encryptedI9FormId);
  }

  async resumeAmendment(token: string): Promise<SectionOne | null> {
    return await this.repo.resumeAmendment(token);
  }

  async fetchInitialData(formId: string, isEncrypted?: boolean): Promise<SectionOne | null> {
    const dto = await this.repo.fetchInitialData(formId, isEncrypted);
    if (!dto) return null;
    return dto;
  }

  async createSectionOne(data: SectionOneForm, status: SectionOneFormState): Promise<SectionOne> {
    return await this.repo.createSectionOne(data, status);
  }

  async updateSectionOne(
    data: SectionOneForm,
    status: SectionOneFormState,
    i9FormId: string
  ): Promise<SectionOne> {
    return await this.repo.updateSectionOne(data, status, i9FormId);
  }

  async markSection1Completed(i9FormId: string): Promise<void> {
    return await this.repo.markSection1Completed(i9FormId);
  }

  async markSection1Started(i9FormId: string, locationId?: string): Promise<void> {
    return await this.repo.markSection1Started(i9FormId, locationId);
  }

  async updateStep(currentStep: SectionOneSteps, i9FormId: string): Promise<SectionOneSteps> {
    return await this.repo.updateStep(currentStep, i9FormId);
  }

  async fetchFormI9Status(i9FormId: string): Promise<SectionOneFormState> {
    return await this.repo.fetchFormI9Status(i9FormId);
  }

  async fetchCountries(): Promise<CountryResponseDto[]> {
    return await this.repo.fetchCountries();
  }

  async fetchEmployeeProvidedDocs(formId: string): Promise<EmployeeProvidedDocs> {
    return await this.repo.fetchEmployeeProvidedDocs(formId);
  }

  async saveEmployeeProvidedDocs(
    command: SaveEmployeeProvidedDocsCommand
  ): Promise<EmployeeProvidedDocs> {
    return await this.repo.saveEmployeeProvidedDocs(command);
  }
}

export default SectionOneUseCase;
